import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { isSafeDatasheetUrl, type ItemCustomsSummaryDto } from '@crm/shared'
import { Badge, CustomsStatusBadge } from '../../../shared/ui/Badge'
import type { ItemColDefsParams, ItemRow } from './itemColDefs.types'
import { useCustomsAnswerView } from './customsAnswerViewStore'
import {
  CustomsValue,
  customsAttentionCell,
  customsCertificateCell,
  customsDocsCell,
  customsFeaturesCell,
  customsDutyCell,
  customsFeeCell,
  customsStaleTitle,
  customsStatusLabel,
  customsTnvedCell,
  customsZnakCell,
  flat,
  isCustomsShown,
  type CustomsCellValue,
} from './itemColDefs.customs.cells'

// Ячейки и поля строки нетарифки — itemColDefs.customs.cells.tsx (вынесено 30.10.2026, правило ≤500 строк).
export * from './itemColDefs.customs.cells'

type Customs = ItemCustomsSummaryDto | null | undefined

/** Короткая подпись ссылки: домен без www. */
function datasheetHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '') || 'даташит'
  } catch {
    return 'даташит'
  }
}

export function DatasheetLink({ url }: { url: unknown }) {
  if (typeof url !== 'string' || !url) return <span className="text-gray-400">—</span>
  // Ссылкой — только http(s): javascript:-адрес в href выполнился бы по клику.
  if (!isSafeDatasheetUrl(url))
    return (
      <span className="text-xs text-red-600" title={url}>
        неверная ссылка
      </span>
    )
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={url}
      onClick={(e) => e.stopPropagation()}
      className="text-xs text-brand-600 hover:text-brand-700 hover:underline"
    >
      ↗ {datasheetHost(url)}
    </a>
  )
}

/** Кто правит даташит в карточке: продажи и ADMIN (на сервере — MANAGER_FIELDS). */
export function canEditDatasheet(isAdmin: boolean, userRole: string | undefined): boolean {
  return isAdmin || userRole === 'MANAGER' || userRole === 'ROP' || userRole === 'ASSISTANT'
}

export function buildDatasheetColDef({
  isAdmin,
  userRole,
}: Pick<ItemColDefsParams, 'isAdmin' | 'userRole'>): ColDef<ItemRow> {
  const editable = canEditDatasheet(isAdmin, userRole)
  return {
    colId: 'datasheetUrl',
    field: 'datasheetUrl',
    headerName: 'Даташит',
    headerTooltip: editable
      ? 'Ссылка на даташит (http:// или https://) — по ней логист запрашивает брокера. Двойной клик — изменить'
      : 'Ссылка на даташит компонента',
    width: 120,
    editable,
    cellRenderer: (p: ICellRendererParams<ItemRow>) => <DatasheetLink url={p.value} />,
    // Проверка http(s) и откат с тостом — в ItemsGrid (onCellValueChanged): здесь
    // только нормализуем ввод, иначе AG Grid молча отбросил бы значение.
    valueSetter: (p: { data: ItemRow; newValue: unknown }) => {
      const raw = String(p.newValue ?? '').trim()
      p.data.datasheetUrl = raw === '' || raw === '—' ? null : raw
      return true
    },
  }
}

function customsCol(
  colId: string,
  field: string,
  headerName: string,
  getCell: (c: Customs) => CustomsCellValue | null,
  extra: Partial<ColDef<ItemRow>> = {},
): ColDef<ItemRow> {
  return {
    colId,
    field,
    headerName,
    sortable: false,
    // Длинный ответ брокера переносится, строка растёт по тексту (просьба Сергея 30.10.2026).
    wrapText: true,
    autoHeight: true,
    cellRenderer: (p: ICellRendererParams<ItemRow>) => (
      <CustomsValue cell={getCell(p.data?.customs as Customs)} wrap />
    ),
    ...extra,
  }
}

/**
 * Длинный текст ответа брокера переносится по ширине колонки; по клику — окно с полным ответом
 * (с 30.10.2026: продажи видят всё, что прислал брокер, как логист).
 */
function OpenableCustomsValue({ data, cell }: { data?: ItemRow; cell: CustomsCellValue | null }) {
  const show = useCustomsAnswerView((s) => s.show)
  const customs = data?.customs as Customs
  if (!cell || !customs) return <CustomsValue cell={cell} wrap />
  return (
    <button
      type="button"
      className="block w-full text-left hover:underline decoration-dotted"
      onClick={(e) => {
        e.stopPropagation()
        show(String(data?.partNumber ?? ''), customs)
      }}
      title="Открыть ответ брокера целиком"
    >
      <CustomsValue cell={cell} wrap />
    </button>
  )
}

function openableCol(
  colId: string,
  headerName: string,
  getCell: (c: Customs) => CustomsCellValue | null,
  extra: Partial<ColDef<ItemRow>> = {},
): ColDef<ItemRow> {
  return customsCol(colId, colId, headerName, getCell, {
    cellRenderer: (p: ICellRendererParams<ItemRow>) => (
      <OpenableCustomsValue data={p.data} cell={getCell(p.data?.customs as Customs)} />
    ),
    ...extra,
  })
}

/** Колонки нетарифки карточки заявки — видны всем ролям, что открывают заявку. */
export function buildCustomsColDefs(): ColDef<ItemRow>[] {
  return [
    {
      colId: 'customsStatus',
      field: 'customsStatusLabel',
      headerName: 'Статус нетарифки',
      headerTooltip:
        'Проверка логистом позиций из-за рубежа: документы, пошлина, сбор, маркировка. Позиция попадает логисту кнопкой «Отправить брокеру»',
      width: 190,
      sortable: false,
      cellRenderer: (p: ICellRendererParams<ItemRow>) => {
        const customs = p.data?.customs as Customs
        if (!isCustomsShown(customs) || !customs?.status)
          return <span className="text-gray-400">—</span>
        const reasons = customs.check?.attentionReasons ?? []
        const statusNote = customs.check?.statusNote?.trim()
        const staleTitle = customsStaleTitle(customs)
        return (
          <span className="inline-flex items-center gap-1">
            <span title={statusNote ? `Логист: ${statusNote}` : undefined}>
              <CustomsStatusBadge status={customs.status} label={customsStatusLabel(customs)} />
            </span>
            {reasons.length > 0 && (
              <span title={`Внимание: ${reasons.join(', ')}`}>
                <Badge color="red">Внимание</Badge>
              </span>
            )}
            {staleTitle && (
              <span title={staleTitle}>
                <Badge color="yellow">Старше месяца</Badge>
              </span>
            )}
          </span>
        )
      },
    },
    customsCol('customsTnved', 'customsTnved', 'ТН ВЭД', customsTnvedCell, { width: 150 }),
    customsCol('customsDuty', 'customsDuty', 'Пошлина', customsDutyCell, { width: 110 }),
    customsCol('customsFee', 'customsFee', 'Сбор', customsFeeCell, { width: 180 }),
    customsCol('customsZnak', 'customsZnak', 'Честный знак', customsZnakCell, {
      width: 140,
      // Фильтр — три значения (поле), копирование — полный текст с датой и «из архива».
      valueFormatter: (p: { data?: ItemRow }) => flat(customsZnakCell(p.data?.customs as Customs)),
    }),
    // Одной строкой, как соседние колонки: по клику — окно с полным ответом брокера.
    openableCol('customsDocs', 'Нетарифка', customsDocsCell, {
      width: 220,
      headerTooltip: 'Разрешительные документы из ответа брокера. Клик — ответ брокера целиком',
    }),
    openableCol('customsCertificate', 'Сертификат', customsCertificateCell, {
      width: 200,
      headerTooltip: '«Сертификат» из ответа брокера. Клик — ответ брокера целиком',
    }),
    openableCol('customsFeatures', 'Особенности', customsFeaturesCell, {
      width: 220,
      headerTooltip: '«Особенности» из ответа брокера. Клик — ответ брокера целиком',
    }),
    customsCol('customsAttention', 'customsAttention', 'Внимание', customsAttentionCell, {
      width: 200,
      headerTooltip: 'Почему позиция помечена красным: повышенный сбор, «Честный знак», документы',
    }),
  ]
}
